import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';

// Matches CompanyDocument::TYPES in the backend (gst, pan, cin, license, udyam, aadhaar).
const DOCUMENT_TYPES = [
  { key: 'gst', label: 'GST Certificate', hint: 'GSTIN registration certificate (PDF or image)' },
  { key: 'cin', label: 'Certificate of Incorporation', hint: 'Certificate of Incorporation / CIN (PDF or image)' },
  { key: 'udyam', label: 'Udyam / Udyog Aadhaar', hint: 'Udyam Registration Certificate (PDF or image)' },
  { key: 'pan', label: 'PAN Card', hint: 'Company PAN card (PDF or image)' },
  { key: 'aadhaar', label: 'Personal Aadhaar', hint: 'Authorized signatory\u2019s personal Aadhaar (PDF or image)' },
  { key: 'license', label: 'Company License', hint: 'Business / trade license (PDF or image)' },
];

// Matches CompanyVerificationService::STATUS_LABELS on the backend.
const STATUS_META = {
  pending_verification: { label: 'Pending Verification', cls: 'bg-gray-100 text-gray-600' },
  documents_submitted: { label: 'Documents Submitted', cls: 'bg-[#FFFBEB] text-[#F59E0B]' },
  under_review: { label: 'Under Review', cls: 'bg-[#EFF6FF] text-[#2563EB]' },
  verified: { label: 'Verified Employer \u2713', cls: 'bg-[#ECFDF5] text-[#10B981]' },
  rejected: { label: 'Rejected', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
  resubmit_required: { label: 'Resubmit Required', cls: 'bg-[#FEF2F2] text-[#EF4444]' },
};

const DOC_STATUS_STYLES = {
  approved: 'bg-[#ECFDF5] text-[#10B981]',
  pending: 'bg-[#FFFBEB] text-[#F59E0B]',
  rejected: 'bg-[#FEF2F2] text-[#EF4444]',
  resubmission_requested: 'bg-[#FEF2F2] text-[#EF4444]',
  missing: 'bg-gray-100 text-gray-500',
};

const DOC_STATUS_LABELS = {
  approved: 'Approved',
  pending: 'Pending Review',
  rejected: 'Rejected',
  resubmission_requested: 'Resubmission Requested',
  missing: 'Not Uploaded',
};

function resolveMediaUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}/storage/${path}`;
}

function StatusBadge({ status }) {
  const key = status || 'missing';
  return (
    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${DOC_STATUS_STYLES[key]}`}>
      {DOC_STATUS_LABELS[key]}
    </span>
  );
}

function OverallStatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending_verification;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold w-fit ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function CompanyVerification() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [loading, setLoading] = useState(true);
  const [statusData, setStatusData] = useState(null); // /company/verification-status payload
  const [message, setMessage] = useState('');

  // Document upload state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [file, setFile] = useState(null);
  const [docNumber, setDocNumber] = useState('');
  const [uploading, setUploading] = useState(false);

  // Official email OTP state
  const [officialEmail, setOfficialEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');

  const fetchAll = async () => {
    try {
      const res = await api.get('/company/verification-status');
      setStatusData(res.data);
    } catch (err) {
      console.error('Failed to fetch verification data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const documents = statusData?.documents || [];
  const documentFor = (type) => documents.find((d) => d.document_type === type);

  const handlePickType = (type) => {
    setSelectedType(type);
    setDropdownOpen(false);
    setFile(null);
    const existing = documentFor(type);
    setDocNumber(existing?.document_number || '');
    setMessage('');
  };

const handleUpload = async (selectedFile) => {
  if (!selectedFile || !selectedType) return;

  setUploading(true);
  setMessage('');

  const data = new FormData();
  data.append('document_type', selectedType);
  if (docNumber) data.append('document_number', docNumber);
  data.append('file', selectedFile);

  try {
    const response = await api.post('/company/documents', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setMessage(response.data.message || 'Document submitted for verification.');
    setFile(null);
    await fetchAll();
  } catch (err) {
    setMessage(err.response?.data?.message || `Failed to upload ${selectedType.toUpperCase()} document.`);
  } finally {
    setUploading(false);
  }
};

const handleFileChange = (e) => {
  const picked = e.target.files[0];
  if (!picked) return;
  setFile(picked);
  handleUpload(picked); // auto-upload on selection
  e.target.value = ''; // allow re-selecting the same file later
};

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!officialEmail) return;
    setSendingOtp(true);
    setEmailMessage('');
    try {
      const res = await api.post('/company/official-email/send-otp', { email: officialEmail });
      setOtpSent(true);
      setEmailMessage(res.data.message || 'Verification code sent.');
    } catch (err) {
      setEmailMessage(err.response?.data?.message || 'Failed to send verification code.');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode) return;
    setVerifyingOtp(true);
    setEmailMessage('');
    try {
      const res = await api.post('/company/official-email/verify-otp', {
        email: officialEmail,
        otp: otpCode,
      });
      setEmailMessage(res.data.message || 'Official email verified.');
      setOtpSent(false);
      setOtpCode('');
      await fetchAll();
    } catch (err) {
      setEmailMessage(err.response?.data?.message || 'Incorrect or expired code.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;

  const overallStatus = statusData?.status || 'pending_verification';
  const isVerified = overallStatus === 'verified';
  const selectedMeta = DOCUMENT_TYPES.find((d) => d.key === selectedType);
  const selectedDoc = selectedType ? documentFor(selectedType) : null;
  const selectedFileUrl = resolveMediaUrl(selectedDoc?.file_path);
  const emailAlreadyVerified = !!statusData?.official_email_verified_at;

  return (
    <div className="min-h-screen bg-[#EFF6FF]">
      <Navbar user={user} />

<div className="max-w-3xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
  <button
    type="button"
    onClick={() => navigate(-1)}
    className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-[#0D1B3D] mb-4"
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
      <polyline points="15 18 9 12 15 6" />
    </svg>
    Back
  </button>

  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
    <h2 className="text-2xl sm:text-3xl font-bold text-[#0D1B3D]">Employer Verification</h2>
    <OverallStatusBadge status={overallStatus} />
  </div>


        <p className="text-sm text-gray-500 mb-6">
          Submit any one or more of the documents below, or verify your official company
          email. As soon as our team approves one document (or your email is verified),
          your profile shows the Verified Employer badge to job seekers.
        </p>

        {message && (
          <div className="bg-[#ECFDF5] text-[#10B981] p-3 rounded-lg mb-6 text-sm font-medium">
            {message}
          </div>
        )}

        {/* --- Document upload --- */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow mb-6">
          <h3 className="font-bold text-[#0D1B3D] text-lg mb-3">Upload Verification Document</h3>

          <div className="relative mb-4">
            <button
              type="button"
              onClick={() => setDropdownOpen((prev) => !prev)}
              className="w-full flex items-center justify-between border rounded-lg px-3 py-2.5 text-sm text-left bg-white hover:border-[#2563EB]"
            >
              <span className={selectedMeta ? 'text-[#0D1B3D] font-medium' : 'text-gray-400'}>
                {selectedMeta ? selectedMeta.label : 'Select a document type...'}
              </span>
              <svg
                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
                className={`w-4 h-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden">
                {DOCUMENT_TYPES.map(({ key, label }) => {
                  const doc = documentFor(key);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => handlePickType(key)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left hover:bg-[#EFF6FF] ${
                        key === selectedType ? 'bg-[#EFF6FF]' : ''
                      }`}
                    >
                      <span className="text-[#0D1B3D]">{label}</span>
                      <StatusBadge status={doc?.status || 'missing'} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {selectedMeta && (
            <>
              <p className="text-xs text-gray-500 mb-3">{selectedMeta.hint}</p>

              {selectedDoc && (
                <div className="mb-3 text-sm text-gray-600 space-y-1">
                  {selectedDoc.document_number && (
                    <p>Document Number: <span className="font-medium text-[#0D1B3D]">{selectedDoc.document_number}</span></p>
                  )}
                  {selectedFileUrl && (
                    <p>
                      <a href={selectedFileUrl} target="_blank" rel="noreferrer" className="text-[#2563EB] underline">
                        View uploaded file
                      </a>
                    </p>
                  )}
                  {(selectedDoc.status === 'rejected' || selectedDoc.status === 'resubmission_requested') && selectedDoc.remarks && (
                    <p className="text-red-500">
                      {selectedDoc.status === 'resubmission_requested' ? 'Resubmission requested: ' : 'Reason: '}
                      {selectedDoc.remarks}
                    </p>
                  )}
                </div>
              )}

             <div className="flex flex-col sm:flex-row sm:items-center gap-3">
  <input
    type="text"
    placeholder={`${selectedMeta.label} Number (optional)`}
    value={docNumber}
    onChange={(e) => setDocNumber(e.target.value)}
    className="border rounded-lg px-3 py-2 text-sm w-full sm:max-w-[220px]"
  />
  <input
    type="file"
    accept=".pdf,.jpg,.jpeg,.png"
    onChange={handleFileChange}
    disabled={uploading}
    className="text-sm w-full sm:w-auto sm:max-w-[220px]"
  />
  {uploading && (
    <span className="text-xs text-gray-400 font-medium">Uploading...</span>
  )}
</div>
            </>
          )}
        </div>

        {/* --- Official Company Email verification --- */}
        <div className="bg-white p-4 sm:p-6 rounded-xl shadow">
          <h3 className="font-bold text-[#0D1B3D] text-lg mb-3">Official Company Email ID</h3>
          <p className="text-xs text-gray-500 mb-3">
            Verify using your official company email address (e.g. name@yourcompany.com).
          </p>

          {emailAlreadyVerified ? (
            <div className="flex items-center gap-2 text-[#10B981] text-sm font-medium">
              <span>&#10003;</span>
              <span>{statusData.official_email} is verified.</span>
            </div>
          ) : (
            <>
              {emailMessage && (
                <div className="mb-3 text-sm font-medium text-[#0D1B3D]">{emailMessage}</div>
              )}

              {!otpSent ? (
                <form onSubmit={handleSendOtp} className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    type="email"
                    placeholder="name@yourcompany.com"
                    value={officialEmail}
                    onChange={(e) => setOfficialEmail(e.target.value)}
                    required
                    className="border rounded-lg px-3 py-2 text-sm w-full sm:max-w-[280px]"
                  />
                  <button
                    type="submit"
                    disabled={sendingOtp || !officialEmail}
                    className="w-full sm:w-auto shrink-0 bg-[#2563EB] text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {sendingOtp ? 'Sending...' : 'Send Code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="4-digit code"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    required
                    className="border rounded-lg px-3 py-2 text-sm w-full sm:max-w-[160px]"
                  />
                  <button
                    type="submit"
                    disabled={verifyingOtp || otpCode.length !== 4}
                    className="w-full sm:w-auto shrink-0 bg-[#10B981] text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                  >
                    {verifyingOtp ? 'Verifying...' : 'Verify Code'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setOtpSent(false); setOtpCode(''); }}
                    className="text-xs text-gray-400 underline"
                  >
                    Change email
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default CompanyVerification;